import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormLabel from "@mui/material/FormLabel";
import FormControl from "@mui/material/FormControl";
import Link from "@mui/material/Link";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import ForgotPassword from "../components/ForgotPassword";
import { useState } from "react";
import FruitifyLogo from "../../../shared-components/FruitifyLogo";
import {
  RegistrationCard,
  FruitifyStack,
  FruitifyContainer,
} from "../../../shared-components/Containers";
import isPasswordValidFormat, { checkIfIsStudent } from "../Utils";
import { useAuthenticationStore } from "../../../stores/auth";
import { Alert } from "@mui/material";
import { useApiClient } from "../../../api";
import { useNavigate } from "react-router-dom";
import { useTrackingAnalytics } from "../../../hooks/useTrackingAnalytics";
import { SubscriptionStatusTypes } from "../../../utils/constants";
import { SingleSignOn } from "../components/SingleSignOn";

export default function Login() {
  const [usernameError, setUsernameError] = useState(false);
  const [usernameErrorMessage, setUsernameErrorMessage] = useState("");
  const [passwordError, setPasswordError] = useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = useState("");
  const [loginFailed, setLoginFailed] = useState(false);
  const [open, setOpen] = useState(false);
  const { setUser, updateUserInfo } = useAuthenticationStore((state) => state);
  const apiClient = useApiClient();
  const navigate = useNavigate();
  const { identifyUser } = useTrackingAnalytics();
  const handleClickOpen = () => {
    setOpen(true);
  };

  const loginWithSso = async (provider, token) => {
    setLoginFailed(false);
    try {
      const data = await apiClient.users.loginWithSso({
        provider,
        token,
      });
      onSuccessfulLogin(data);
    } catch (err) {
      console.error(err);
      setLoginFailed(true);
    }
  };

  const onSuccessfulLogin = async (data) => {
    const { token, refreshToken, refreshExpiresIn } = data;
    setUser({
      token,
      refreshToken,
      refreshExpiresIn,
    });
    const userData = await apiClient.users.me();
    const isIndependent =
      userData.subscriptionStatus === SubscriptionStatusTypes.Independent;
    const isStudent = checkIfIsStudent({ user: { info: userData } });
    identifyUser(userData.email, isIndependent, isStudent);
    updateUserInfo(userData);
    const params = new URLSearchParams(location.search);
    console.log(params);
    const redirectTo = params.get("redirect") || "/dashboard";
    console.log(redirectTo);
    window.location.href = redirectTo;
  };

  const handleSubmit = async (e) => {
    setLoginFailed(false);
    e.preventDefault(); // Prevent default form submission

    const data = new FormData(e.currentTarget);
    const username = data.get("username");
    const password = data.get("password");

    const isValid = validateInputs({ username, password }); // Call validation

    if (!isValid) return; // Stop if not valid
    try {
      const data = await apiClient.users.login({
        email: username.toLowerCase(),
        password,
      });
      onSuccessfulLogin(data);
    } catch (err) {
      console.error(err);
      setLoginFailed(true);
    }
  };

  const validateInputs = ({ username, password }) => {
    let isValid = true;

    if (!username) {
      setUsernameError(true);
      setUsernameErrorMessage("Username must be provided");
      return false;
    } else {
      setUsernameError(false);
      setUsernameErrorMessage("");
    }

    isValid = isPasswordValidFormat(
      password,
      setPasswordError,
      setPasswordErrorMessage,
    );

    return isValid;
  };

  return (
    <FruitifyContainer>
      <FruitifyStack
        direction="column"
        justifyContent="space-between"
        data-testid={"fruitify-login-page"}
        sx={{ height: "100%" }}
      >
        <RegistrationCard variant="outlined">
          <Stack direction={"row"} justifyContent={"center"}>
            <FruitifyLogo />
          </Stack>
          <Typography
            component="h1"
            variant="h4"
            sx={{ width: "100%", fontSize: "clamp(2rem, 10vw, 2.15rem)" }}
          >
            Sign in
          </Typography>
          <SingleSignOn onSuccess={loginWithSso} />
          {loginFailed && <Alert severity="error">Failed to login</Alert>}
          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              gap: 2,
            }}
          >
            <FormControl>
              <FormLabel htmlFor="username">Email or Username</FormLabel>
              <TextField
                error={usernameError}
                helperText={usernameErrorMessage}
                id="username"
                type="username"
                name="username"
                placeholder="username"
                autoComplete="username"
                autoFocus
                required
                fullWidth
                variant="outlined"
                color={usernameError ? "error" : "primary"}
              />
            </FormControl>
            <FormControl>
              <FormLabel htmlFor="password">Password</FormLabel>
              <TextField
                error={passwordError}
                helperText={passwordErrorMessage}
                name="password"
                placeholder="••••••"
                type="password"
                id="password"
                autoComplete="current-password"
                autoFocus
                required
                fullWidth
                variant="outlined"
                color={passwordError ? "error" : "primary"}
              />
            </FormControl>
            <ForgotPassword open={open} handleClose={() => setOpen(false)} />
            <Button type="submit" fullWidth variant="contained">
              Sign in
            </Button>
            <Button
              onClick={() => navigate(`/auth/register/free-account-request`)}
              fullWidth
              variant="contained"
              color={"secondary"}
            >
              Join for free
            </Button>
            <Link
              component="button"
              type="button"
              onClick={handleClickOpen}
              variant="body2"
              sx={{ alignSelf: "center" }}
            >
              Forgot your password?
            </Link>
          </Box>
        </RegistrationCard>
      </FruitifyStack>
    </FruitifyContainer>
  );
}
